import { TimePickerProps } from "./types";
import type { DatePickerProps as ReactDatePickerProps } from "react-datepicker";
import { useTranslation } from "react-i18next";

import { getAppLanguage } from "@lib/store";
import { isToday, parseISO, setHours, setMinutes } from "date-fns";
import { nl, fr, enUS } from "date-fns/locale";

import { default as ReactDatePicker, registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import styles from "../styles.module.scss";

import { useAppSelector } from "@hooks";
import { Div } from "@quarks";

registerLocale("en", enUS);
registerLocale("fr", fr);
registerLocale("nl", nl);

export const TimePicker = ({ date, minDate, setDate, "data-testid": dataTestId }: TimePickerProps) => {
  const locale = useAppSelector(getAppLanguage);
  const { t } = useTranslation();

  const handleOnChangeRaw: NonNullable<ReactDatePickerProps["onChangeRaw"]> = rawValue => {
    const input = rawValue?.target;

    if (!(input instanceof HTMLInputElement)) {
      return;
    }

    const value = input.value;
    const [hours, minutes] = value.split(":");

    if (value.length === 2 && !isNaN(parseInt(hours))) {
      input.value += ":";
    }

    if (
      (!(value.split(":").length >= 3) || !value.includes(":")) &&
      !isNaN(parseInt(hours)) &&
      !isNaN(parseInt(minutes))
    ) {
      if (value.length === 2) {
        setDate(prevDate => setHours(setMinutes(parseISO(prevDate), 0), parseInt(hours)).toISOString());
      }

      if (value.length === 5) {
        setDate(prevDate => setHours(setMinutes(parseISO(prevDate), parseInt(minutes)), parseInt(hours)).toISOString());
      }
    }
  };

  return (
    <Div
      position="relative"
      data-testid={dataTestId}>
      <ReactDatePicker
        calendarClassName={styles.datePicker}
        className={styles.input}
        dateFormat="HH:mm"
        enableTabLoop={false}
        locale={locale}
        maxTime={setMinutes(setHours(parseISO(date), 23), 59)}
        minDate={parseISO(minDate)}
        minTime={isToday(parseISO(date)) ? parseISO(minDate) : setMinutes(setHours(parseISO(date), 0), 0)}
        onChange={() => void 0}
        onChangeRaw={handleOnChangeRaw}
        onKeyDown={(e: any) => {
          if (e.key === "Backspace" && e.currentTarget.value.length === 3) {
            e.currentTarget.value = e.currentTarget.value.substring(0, 2);
          }
        }}
        open={false}
        popperPlacement="bottom"
        preventOpenOnFocus={true}
        selected={parseISO(date)}
        showPopperArrow={false}
        showTimeSelect={false}
        showTimeSelectOnly={true}
        timeCaption={t("meeting:Time")}
        timeIntervals={15}
      />
    </Div>
  );
};
